import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type {
  PosterProfile,
  HookType,
  NewsSource,
  SocialPostSource,
  CampaignSource,
  AgentContentRequest,
  AgentContentResponse,
} from "@/types/content-studio";

// ---------------------------------------------------------------------------
// Team Profiles (senders)
// ---------------------------------------------------------------------------

export function useTeamProfiles() {
  return useQuery({
    queryKey: ["content-studio", "team-profiles"],
    queryFn: async (): Promise<PosterProfile[]> => {
      const { data, error } = await supabase
        .from("team_profiles" as any)
        .select("id, full_name, job_title, company, voice_description, writing_style, is_default, is_active, sort_order")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data || []).map((p: any) => ({
        id: p.id,
        name: p.full_name,
        title: p.job_title,
        company: p.company,
        voice_description: p.voice_description,
        writing_style: p.writing_style,
        is_default: p.is_default || false,
      }));
    },
  });
}

// ---------------------------------------------------------------------------
// Hook Library
// ---------------------------------------------------------------------------

export function useHookLibrary() {
  return useQuery({
    queryKey: ["content-studio", "hook-library"],
    queryFn: async (): Promise<HookType[]> => {
      const { data, error } = await supabase
        .from("linkedin_hook_library" as any)
        .select("*")
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data || []) as unknown as HookType[];
    },
  });
}

// ---------------------------------------------------------------------------
// News Sources (for source selection)
// ---------------------------------------------------------------------------

export function useNewsSources(limit = 30) {
  return useQuery({
    queryKey: ["content-studio", "news-sources", limit],
    queryFn: async (): Promise<NewsSource[]> => {
      const { data, error } = await supabase
        .from("v_news_intelligence_table" as any)
        .select("id, title, url, summary, short_summary, executive_briefing, opportunity_analysis, action_items, outreach_guidance, match_reasoning, source_feed, published_at, signal_type, matched_keywords, matched_competitors, matched_accounts, matched_categories, prospect_company_name, is_prospect_opportunity, is_competitor_alert, linkedin_score, linkedin_post_draft, enriched_content, market_context, companies_mentioned, enrichment_metadata, enriched_at, author")
        .order("published_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data || []).map((n: any) => ({
        id: n.id,
        title: n.title,
        url: n.url ?? null,
        summary: n.summary,
        short_summary: n.short_summary,
        executive_briefing: n.executive_briefing,
        opportunity_analysis: n.opportunity_analysis,
        action_items: n.action_items ?? null,
        outreach_guidance: n.outreach_guidance,
        match_reasoning: n.match_reasoning ?? null,
        source_feed: n.source_feed,
        published_at: n.published_at,
        signal_type: n.signal_type,
        matched_keywords: n.matched_keywords ?? null,
        matched_competitors: n.matched_competitors ?? null,
        matched_accounts: n.matched_accounts ?? null,
        matched_categories: n.matched_categories ?? null,
        prospect_company_name: n.prospect_company_name ?? null,
        is_prospect_opportunity: n.is_prospect_opportunity ?? null,
        is_competitor_alert: n.is_competitor_alert ?? null,
        linkedin_score: n.linkedin_score ?? null,
        linkedin_post_draft: n.linkedin_post_draft ?? null,
        enriched_content: n.enriched_content ?? null,
        market_context: n.market_context ?? null,
        companies_mentioned: n.companies_mentioned ?? null,
        enrichment_metadata: n.enrichment_metadata ?? null,
        enriched_at: n.enriched_at ?? null,
        author: n.author ?? null,
      }));
    },
  });
}

// ---------------------------------------------------------------------------
// Social Post Sources (for source selection)
// ---------------------------------------------------------------------------

export function useSocialSources(limit = 30) {
  return useQuery({
    queryKey: ["content-studio", "social-sources", limit],
    queryFn: async (): Promise<SocialPostSource[]> => {
      const { data, error } = await supabase
        .from("social_listening_post_cards" as any)
        .select("post_url, post_author, monitoring_profile, post_content, post_date, engagement_count, likes, comments")
        .order("post_date", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data || []).map((s: any) => ({
        post_id: s.post_url || crypto.randomUUID(),
        post_url: s.post_url,
        poster_name: s.post_author,
        monitoring_profile: s.monitoring_profile,
        content_preview: s.post_content?.substring(0, 200) || null,
        full_content: s.post_content,
        post_date: s.post_date,
        likes: s.likes || 0,
        comments: s.comments || 0,
        engagement_count: s.engagement_count || 0,
      }));
    },
  });
}

// ---------------------------------------------------------------------------
// Campaign Sources (for source selection)
// ---------------------------------------------------------------------------

export function useCampaignSources() {
  return useQuery({
    queryKey: ["content-studio", "campaign-sources"],
    queryFn: async (): Promise<CampaignSource[]> => {
      const { data, error } = await supabase
        .from("campaign_tracker_campaigns" as any)
        .select("campaign_id, campaign_name, description, campaign_status, platform, territory, executive_name, linkedin_leads, linkedin_sent, linkedin_accepted, linkedin_replied, linkedin_acc_rate, linkedin_reply_rate")
        .order("start_date", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data || []) as unknown as CampaignSource[];
    },
  });
}

// ---------------------------------------------------------------------------
// Generate Content (calls VPS Agent via proxy edge function)
// ---------------------------------------------------------------------------

export function useGenerateContent() {
  return useMutation({
    mutationFn: async (request: AgentContentRequest): Promise<AgentContentResponse> => {
      // Use session token if available, fall back to anon key
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        apikey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRyenhtb2tndXB0dndwdHVxamN2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE1NTA0NTcsImV4cCI6MjA4NzEyNjQ1N30.j82imh1o82JeWGtfcN7FszSa0LNcImDXski4qOpqiw0",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        "https://drzxmokguptvwptuqjcv.supabase.co/functions/v1/generate-content",
        {
          method: "POST",
          headers,
          body: JSON.stringify(request),
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: "Request failed" }));
        throw new Error(err.error || err.detail || `HTTP ${response.status}`);
      }

      return response.json();
    },
  });
}

// ---------------------------------------------------------------------------
// Save to content_generation_history
// ---------------------------------------------------------------------------

export function useSaveGeneration() {
  return useMutation({
    mutationFn: async (params: {
      content_type: string;
      generated_content: string;
      team_profile_id: string | null;
      hook_type: string | null;
      source_custom_input: string | null;
      context_notes: string | null;
    }) => {
      const { error } = await supabase
        .from("content_generation_history" as any)
        .insert({
          ...params,
          is_saved: true,
        });
      if (error) throw error;
    },
  });
}
